import * as React from 'react'

/** Normalize wheel deltas to CSS pixels (handles line/page deltaMode). */
export function normalizeWheelDelta(event: WheelEvent): { deltaX: number; deltaY: number } {
  let { deltaX, deltaY } = event
  if (event.deltaMode === 1) {
    deltaX *= 16
    deltaY *= 16
  } else if (event.deltaMode === 2) {
    deltaX *= window.innerWidth
    deltaY *= window.innerHeight
  }
  return { deltaX, deltaY }
}

export function canScrollElement(el: HTMLElement, deltaY: number): boolean {
  const { scrollTop, scrollHeight, clientHeight } = el
  // Subpixel rounding: treat near-equal sizes as non-scrollable.
  if (scrollHeight <= clientHeight + 1) return false
  if (deltaY < 0) return scrollTop > 0
  if (deltaY > 0) return scrollTop + clientHeight < scrollHeight - 1
  return false
}

/**
 * Editor panes that trap wheel (textarea / preview). Other editor chrome
 * (toolbar, gutters) must not be handled — the browser already page-scrolls there.
 */
export function getTrappingScrollable(
  target: EventTarget | null,
  root: HTMLElement
): HTMLElement | null {
  if (!(target instanceof Element) || !root.contains(target)) return null

  const htmlWrap = target.closest('.html-wrap')
  if (htmlWrap instanceof HTMLElement && root.contains(htmlWrap)) return htmlWrap

  const mdInput = target.closest('.sec-md .input')
  if (mdInput instanceof HTMLElement && root.contains(mdInput)) return mdInput

  return null
}

function isScrollableOverflowY(style: CSSStyleDeclaration): boolean {
  const overflowY = style.overflowY
  return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
}

/** Prefer a nested layout scroller; fall back to the document scrolling element. */
export function getPageScrollElement(from: HTMLElement): Element {
  let node: HTMLElement | null = from.parentElement
  while (node && node !== document.documentElement && node !== document.body) {
    const style = window.getComputedStyle(node)
    if (isScrollableOverflowY(style) && node.scrollHeight > node.clientHeight + 1) {
      return node
    }
    node = node.parentElement
  }
  return document.scrollingElement || document.documentElement
}

/**
 * Instant page/container scroll. Temporarily forces scroll-behavior: auto because
 * `html { scroll-behavior: smooth }` can animate scrollTop mutations and cancel
 * rapid wheel ticks.
 */
export function applyInstantScroll(el: Element, deltaX: number, deltaY: number) {
  const styled =
    el === document.scrollingElement || el === document.documentElement || el === document.body
      ? document.documentElement
      : el instanceof HTMLElement
        ? el
        : null

  const previousBehavior = styled?.style.scrollBehavior
  if (styled) styled.style.scrollBehavior = 'auto'

  el.scrollTop += deltaY
  el.scrollLeft += deltaX

  if (styled) {
    styled.style.scrollBehavior = previousBehavior ?? ''
  }
}

/**
 * Embedded markdown editors trap wheel inside textarea/preview panes.
 *
 * Take ownership of wheel over those panes:
 * - pane still has room → scroll the pane
 * - at edge / no overflow → scroll the page (or nearest scroll parent)
 *
 * This avoids browsers swallowing the gesture on overflow:auto + overscroll-behavior,
 * and avoids fighting `html { scroll-behavior: smooth }`.
 */
export function useMarkdownEditorPageScroll(
  containerRef: React.RefObject<HTMLElement | null>,
  enabled: boolean
) {
  React.useEffect(() => {
    if (!enabled) return

    const root = containerRef.current
    if (!root) return

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.defaultPrevented) return

      const editorEl = root.querySelector('.rc-md-editor')
      if (editorEl?.classList.contains('full')) return

      const scrollable = getTrappingScrollable(event.target, root)
      // Toolbar / non-trapping chrome: leave native page scroll alone.
      if (!scrollable) return

      const { deltaX, deltaY } = normalizeWheelDelta(event)
      if (deltaX === 0 && deltaY === 0) return

      // Own the gesture so native overscroll cannot eat it.
      event.preventDefault()

      if (canScrollElement(scrollable, deltaY)) {
        applyInstantScroll(scrollable, deltaX, deltaY)
        return
      }

      applyInstantScroll(getPageScrollElement(root), deltaX, deltaY)
    }

    root.addEventListener('wheel', handleWheel, { passive: false })
    return () => root.removeEventListener('wheel', handleWheel)
  }, [containerRef, enabled])
}
